import type { ReactNode } from 'react';
import { issueCounts } from '../../types/validation';
import type { ValidationReport } from '../../types/validation';

interface Props {
  open: boolean;
  title: string;
  typeLabel: string;
  validation?: ValidationReport | null;
  onClose: () => void;
  footer: ReactNode;
  children: ReactNode;
  wide?: boolean;
}

export default function NodeDialogShell({
  open,
  title,
  typeLabel,
  validation,
  onClose,
  footer,
  children,
  wide = false,
}: Props) {
  if (!open) return null;

  const counts = issueCounts(validation);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className={`relative flex max-h-[90vh] w-full flex-col overflow-hidden rounded-xl border border-border bg-surface-raised shadow-2xl ${
          wide ? 'max-w-6xl' : 'max-w-2xl'
        }`}
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-text">{title}</h2>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span className="badge-accent-on-dark px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide">
                {typeLabel}
              </span>
              {counts.errors > 0 && (
                <span className="status-pill badge-danger-on-dark">
                  {counts.errors} error{counts.errors !== 1 ? 's' : ''}
                </span>
              )}
              {counts.warnings > 0 && (
                <span className="status-pill badge-warning-on-dark">
                  {counts.warnings} warning{counts.warnings !== 1 ? 's' : ''}
                </span>
              )}
              {counts.errors === 0 && counts.warnings === 0 && (
                <span className="status-pill-saved">Valid</span>
              )}
            </div>
          </div>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-border px-5 py-3">
          {footer}
        </footer>
      </div>
    </div>
  );
}
